/**
 * HomePageNew - New sidebar-based layout for the home page
 * Uses AppShell for consistent navigation
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTeamMessages } from "@/contexts/TeamMessageContext";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import TrialBanner from "@/components/TrialBanner";
import FeatureGate from "@/components/FeatureGate";
import HeroCarousel from "@/components/dashboard/HeroCarousel";
import AutoInvoicingTab from "@/components/invoicing/AutoInvoicingTab";
import CenterCommunicationTab from "@/components/communications/CenterCommunicationTab";
import ParentCommunicationCenterTab from "@/components/communications/ParentCommunicationCenterTab";
import { ParentCommunicationTab } from "@/components/staff-reports/ParentCommunicationTab";
import { FeedbackSuggestionsTab } from "@/components/feedback/FeedbackSuggestionsTab";
import AppShell from "@/components/layout/AppShell";
import { DailyListTab } from "@/components/dashboard/DailyListTab";
import { PaymentsTab } from "@/components/dashboard/PaymentsTab";
import { StillInCenterByCategory } from "@/components/daily-signin/StillInCenterByCategory";
import axios from "axios";
import { format } from "date-fns";
import { 
  Users, Calendar as CalendarIcon, ListChecks, CreditCard, Clock, 
  Megaphone, MessageSquare, ChevronDown, UserCog, 
  Database, Building2, Shield, ArrowUpCircle, LogOut, Lightbulb, FileText,
  CheckCircle, X, Folder, Users2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PRIMARY_OWNER_EMAIL = "todayskidz@comcast.net";

// Touch AND hover friendly count component - works on desktop (hover) AND mobile (tap)
// Reusable tooltip for showing children names on hover/tap
const HoverCount = ({ label, count, children = [], colorClass = "text-ink-main" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  
  // Close tooltip when clicking/tapping outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);
  
  // Cleanup hover timeout
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);
  
  // If no children to show, just render plain text
  if (count === 0 || children.length === 0) {
    return (
      <div className="flex justify-between border-b border-line/60 pb-2 last:border-0 last:pb-0">
        <span className="text-ink-muted">{label}</span>
        <span className={`font-semibold ${colorClass}`}>{count}</span>
      </div>
    );
  }
  
  const handleMouseEnter = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(true);
    }, 200);
  };
  
  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovering(false);
  };
  
  const handleTap = (e) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  const showTooltip = isOpen || isHovering;

  return (
    <div 
      className="relative flex justify-between border-b border-line/60 pb-2 last:border-0 last:pb-0 cursor-pointer select-none" 
      ref={containerRef}
      onClick={handleTap}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchEnd={handleTap}
    >
      <span className="text-ink-muted hover:underline">{label}</span>
      <span 
        className={`font-semibold ${colorClass} hover:underline`}
      >
        {count}
      </span>
      
      {/* Tooltip popup */}
      {showTooltip && (
        <div 
          className="absolute z-[9999] right-0 top-full mt-2 bg-gray-900 text-white p-1.5 rounded shadow-xl min-w-[130px] max-w-[200px] whitespace-normal"
          style={{ animation: 'fadeIn 0.15s ease-out' }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={handleMouseLeave}
        >
          <button 
            onClick={(e) => { e.stopPropagation(); setIsOpen(false); setIsHovering(false); }}
            className="absolute top-1 right-1 p-1 hover:bg-gray-700 rounded"
          >
            <X className="w-3 h-3" />
          </button>
          <p className="font-bold mb-1 text-[11px] border-b border-gray-600 pb-1 pr-4">{label} ({count})</p>
          <ul className="text-[11px] space-y-0.5 max-h-48 overflow-y-auto">
            {children.map((name, idx) => (
              <li key={idx} className="break-words">• {name}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
function StatCard({ title, value, tag, tagStyle, icon: Icon }) {
  return (
    <div className="bg-white rounded-xl shadow-card border-2 border-blue-300 p-5 hover:shadow-lg hover:border-blue-400 transition-all">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {Icon && (
            <div className="p-2.5 bg-blue-100 rounded-xl">
              <Icon className="w-5 h-5 text-blue-600" />
            </div>
          )}
          <div>
            <div className="text-sm text-ink-muted">{title}</div>
            <div className="text-3xl font-bold text-ink-main mt-1">{value}</div>
          </div>
        </div>
        {tag && (
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${tagStyle || "bg-brand-highlight/20 text-ink-main"}`}>
            {tag}
          </span>
        )}
      </div>
    </div>
  );
}

// Stat Group Card Component with hover tooltips
function StatGroupCard({ title, color, lines, icon: Icon }) {
  return (
    <div className="bg-white rounded-xl shadow-card border-2 border-blue-300 p-5 hover:shadow-lg hover:border-blue-400 transition-all">
      <div className={`flex items-center gap-2 text-sm font-semibold uppercase tracking-wide ${color || "text-brand-primary"}`}>
        {Icon && <Icon className="w-4 h-4" />}
        {title}
      </div>
      <div className="mt-3 space-y-2 text-sm">
        {lines.map((l, idx) => (
          <HoverCount 
            key={idx}
            label={l.label}
            count={l.value}
            children={l.children || []}
            colorClass="text-ink-main"
          />
        ))}
      </div>
    </div>
  );
}

// Action Button Component - More prominent styling
function ActionButton({ label, icon: Icon, onClick, variant = "default" }) {
  const variants = {
    default: "bg-white border-brand-primary/20 hover:border-brand-primary hover:bg-brand-primary/5 text-brand-primary",
    primary: "bg-brand-primary text-white hover:bg-brand-primary/90",
    secondary: "bg-brand-secondary text-white hover:bg-brand-secondary/90",
    highlight: "bg-brand-highlight text-white hover:bg-brand-highlight/90",
    accent: "bg-brand-accent text-white hover:bg-brand-accent/90",
  };

  return (
    <button 
      onClick={onClick}
      className={`
        flex items-center gap-2.5 px-5 py-3 rounded-xl border-2 shadow-sm 
        hover:shadow-md transition-all duration-200 text-sm font-semibold
        ${variants[variant]}
      `}
    >
      {Icon && <Icon className="w-5 h-5" />}
      <span>{label}</span>
    </button>
  );
}

// Tab Button Component - Enhanced with blue theme
function TabButton({ label, active, onClick, icon: Icon, badge, isCalendar }) {
  return (
    <button
      onClick={onClick}
      className={`
        relative flex items-center gap-2.5 px-5 py-2.5 rounded-xl text-sm font-semibold 
        border-2 transition-all duration-300 whitespace-nowrap
        ${active && isCalendar
          ? "bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 text-white border-transparent shadow-xl shadow-blue-500/40 scale-105 ring-2 ring-blue-300 ring-offset-2"
          : active
          ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white border-transparent shadow-lg shadow-blue-500/30 scale-[1.02]"
          : "bg-white text-gray-700 border-blue-200 hover:border-blue-400 hover:bg-blue-50 hover:shadow-md"
        }
      `}
    >
      {Icon && <Icon className={`w-4 h-4 ${active ? "text-white" : "text-blue-500"}`} />}
      {label}
      {badge > 0 && (
        <span className={`absolute -top-2 -right-2 min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center ${
          active ? "bg-white text-blue-600" : "bg-red-500 text-white"
        }`}>
          {badge}
        </span>
      )}
    </button>
  );
}

const HomePageNew = () => {
  const navigate = useNavigate();
  const { user, organization, logout } = useAuth();
  const { subscription } = useSubscription();
  const isViewOnly = subscription?.status === 'expired' || 
                     (subscription?.status === 'trial' && (subscription?.trialDaysRemaining ?? 0) <= 0);
  const { language } = useLanguage();
  useTeamMessages(); // Keep context subscription active
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState("calendar");
  const [loading, setLoading] = useState(true);
  const [gettingStartedOpen, setGettingStartedOpen] = useState(false);
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const [teamMessages, setTeamMessages] = useState([]);
  
  // Stats state
  const [totalChildren, setTotalChildren] = useState(0);
  const [allChildren, setAllChildren] = useState([]);
  const [todaySignIns, setTodaySignIns] = useState([]);
  const [amBusPickUpTime, setAmBusPickUpTime] = useState(null);
  const [docNotificationCount, setDocNotificationCount] = useState(0);
  const [complianceScore, setComplianceScore] = useState(null);
  const [showComplianceToStaff, setShowComplianceToStaff] = useState(false);
  const [togglingVisibility, setTogglingVisibility] = useState(false);

  const isOwner = user?.role === 'owner' || user?.role?.toLowerCase() === 'co-owner';
  const isOwnerOnly = user?.role === 'owner'; // Excludes co-owners
  const isPrimaryOwner = user?.email?.toLowerCase() === PRIMARY_OWNER_EMAIL.toLowerCase();

  // Role-based permissions matrix (loaded from Owner Tools settings)
  const [permissionMatrix, setPermissionMatrix] = useState({});
  useEffect(() => {
    axios.get(`${API}/owner/permissions`)
      .then(r => setPermissionMatrix(r.data?.permissions || {}))
      .catch(() => {});
  }, []);

  const checkMatrix = (featureKey) => {
    if (user?.role === 'owner') return true;
    const rolePerms = permissionMatrix[featureKey];
    if (!rolePerms) return true;
    return rolePerms[user?.role] !== false;
  };

  // Staff permissions — driven by the Owner Tools permissions matrix
  const canAccessParentCommunications = user?.role === 'owner' || checkMatrix('parent_sms_communications');
  const canAccessCenterCommunications = user?.role === 'owner' || checkMatrix('family_communications_hub');
  const canManagePayments = user?.role === 'owner' || checkMatrix('payments');

  // Get page title based on active tab
  const getPageTitle = () => {
    const titles = {
      'calendar': language === 'es' ? 'Panel de Control' : 'Dashboard',
      'daily': language === 'es' ? 'Lista Diaria' : 'Daily List',
      'payments': language === 'es' ? 'Pagos' : 'Payments',
      'auto-invoicing': language === 'es' ? 'Facturación Automática' : 'Auto Invoicing',
      'parent-communication-center': language === 'es' ? 'Centro Comunicación' : 'Parent Communication Center Email',
      'feedback': language === 'es' ? 'Comentarios' : 'Feedback'
    };
    
    const baseTitle = titles[activeTab] || 'Dashboard';
    
    // Add compliance score badge for Dashboard tab (owners only) - Green gradient style
    if (activeTab === 'calendar' && isOwner && complianceScore !== null) {
      return (
        <span className="flex items-center gap-2">
          <span>{baseTitle}</span>
          {/* Compliance % badge — clickable to dashboard */}
          <div className="relative group">
            <span 
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-md cursor-pointer hover:from-emerald-400 hover:to-green-500 transition-all"
              onClick={() => navigate('/compliance-dashboard')}
            >
              <CheckCircle className="w-4 h-4" />
              <span>{Math.round(complianceScore)}% Compliant</span>
            </span>
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap text-xs font-black uppercase tracking-wide bg-gradient-to-r from-emerald-500 to-green-600 text-white">
              NAVIGATE TO THE COMPLIANCE DASHBOARD
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-emerald-500 rotate-45" />
            </div>
          </div>
          {/* Share with staff toggle — right next to the % */}
          <div className="relative group">
            <button
              onClick={async (e) => {
                e.stopPropagation();
                if (togglingVisibility) return;
                setTogglingVisibility(true);
                try {
                  const next = !showComplianceToStaff;
                  await axios.patch(`${API}/organizations/compliance-visibility`, { showComplianceToStaff: next });
                  setShowComplianceToStaff(next);
                } catch {}
                setTogglingVisibility(false);
              }}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border transition-all ${
                showComplianceToStaff
                  ? 'bg-blue-600 border-blue-600 text-white shadow-md'
                  : 'bg-white border-2 border-blue-400 text-gray-500 hover:border-blue-600 hover:text-blue-600'
              }`}
              data-testid="home-compliance-visibility-toggle"
            >
              <Users2 className="w-3.5 h-3.5" />
              <span>{showComplianceToStaff ? 'Shared' : 'Share'}</span>
            </button>
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap text-xs font-black uppercase tracking-wide bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              {showComplianceToStaff ? 'STAFF CAN SEE COMPLIANCE % IN NAV — CLICK TO HIDE' : 'CLICK TO SHARE COMPLIANCE % WITH STAFF AS A TEAM MOTIVATOR'}
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-blue-600 rotate-45" />
            </div>
          </div>
        </span>
      );
    }
    
  };

  // Welcome back toast — once per login session
  useEffect(() => {
    if (!sessionStorage.getItem('welcomeShown')) {
      sessionStorage.setItem('welcomeShown', '1');
      toast.success('Welcome back!', { position: 'top-center' });
    }
  }, []);

  // Listen for tab change events from sidebar navigation
  useEffect(() => {
    const handleTabChange = (event) => {
      setActiveTab(event.detail);
    };
    window.addEventListener('changeTab', handleTabChange);
    return () => window.removeEventListener('changeTab', handleTabChange);
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Dashboard Home "Still Here Past Reminder Time" badge — same status-today
  // endpoint and 60s refresh pattern as the Manage Children badge; skipped
  // entirely on Basic since the feature isn't available there.
  const { hasAccess: hasStillHereAccess } = useFeatureAccess('still_here_reminders');
  const [pastReminderChildIds, setPastReminderChildIds] = useState([]);

  useEffect(() => {
    if (!hasStillHereAccess) return;
    const fetchPastReminderStatus = () => {
      axios.get(`${API}/still-here-reminders/status-today`)
        .then(r => setPastReminderChildIds(r.data.childIds || []))
        .catch(() => {});
    };
    fetchPastReminderStatus();
    const interval = setInterval(fetchPastReminderStatus, 60000);
    return () => clearInterval(interval);
  }, [hasStillHereAccess]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [childrenRes, signInsRes] = await Promise.all([
        axios.get(`${API}/children`),
        axios.get(`${API}/signins?date=${format(new Date(), 'yyyy-MM-dd')}`)
      ]);
      
      // Filter by 'active' field (not 'status')
      const activeChildren = childrenRes.data.filter(c => c.active !== false);
      setAllChildren(activeChildren);
      setTotalChildren(activeChildren.length);
      setTodaySignIns(signInsRes.data || []);

      // Fetch AM bus pick-up time for School Age card
      try {
        const busRes = await axios.get(`${API}/bus-tracking/${format(new Date(), 'yyyy-MM-dd')}`);
        const tracking = busRes.data.tracking || {};
        setAmBusPickUpTime(tracking.am_pickup?.arrivalTime || null);
      } catch { setAmBusPickUpTime(null); }
      
      // Fetch document notifications
      try {
        const notifRes = await axios.get(`${API}/document-notifications`);
        setDocNotificationCount(notifRes.data.count || 0);
      } catch (e) {
        console.log("No document notifications");
      }
      
      // Fetch compliance score (for owners/co-owners only)
      if (isOwner) {
        try {
          const [complianceRes, visRes] = await Promise.all([
            axios.get(`${API}/compliance/readiness-score`),
            axios.get(`${API}/organizations/compliance-visibility`)
          ]);
          setComplianceScore(complianceRes.data?.score || 0);
          setShowComplianceToStaff(visRes.data?.showComplianceToStaff || false);
        } catch (e) {
          console.log("Could not fetch compliance score");
        }
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      navigate(`/signin/${format(date, 'yyyy-MM-dd')}`);
    }
  };

  const handleTodaySignIn = () => {
    navigate(`/signin/${format(new Date(), 'yyyy-MM-dd')}`);
  };

  // Fetch team messages when popup opens
  useEffect(() => {
    const fetchTeamMessages = async () => {
      if (showMessagePopup) {
        try {
          const today = format(new Date(), 'yyyy-MM-dd');
          const res = await axios.get(`${API}/team-messages/${today}`);
          setTeamMessages(res.data || []);
        } catch (error) {
          console.log("Could not fetch team messages");
        }
      }
    };
    fetchTeamMessages();
  }, [showMessagePopup]);

  // Calculate stats - handle both old format ('Full Time') and new format ('full_time')
  const fullTimeChildren = allChildren.filter(c => 
    c.category === 'full_time' || c.category === 'regular' || c.category === 'Full Time'
  );
  const schoolAgeChildren = allChildren.filter(c => 
    c.category === 'school_age' || c.category === 'School Age'
  );
  const partTimeChildren = allChildren.filter(c => 
    c.category === 'part_time' || c.category === 'Part Time'
  );
  const afterSchoolChildren = allChildren.filter(c => 
    c.category === 'after_school' || c.category === 'After School'
  );

  // Filter sign-ins by category - handle both formats
  const fullTimeSignIns = todaySignIns.filter(s => 
    s.category === 'full_time' || s.category === 'regular' || s.category === 'Full Time'
  );
  const schoolAgeSignIns = todaySignIns.filter(s => 
    s.category === 'school_age' || s.category === 'School Age'
  );
  const partTimeSignIns = todaySignIns.filter(s => 
    s.category === 'part_time' || s.category === 'Part Time'
  );
  const afterSchoolSignIns = todaySignIns.filter(s => 
    s.category === 'after_school' || s.category === 'After School'
  );

  // Full Time counts - use dropOffTime/pickUpTime fields
  const fullTimeSignedIn = fullTimeSignIns.filter(s => s.dropOffTime).length;
  const fullTimeSignedOut = fullTimeSignIns.filter(s => s.dropOffTime && s.pickUpTime).length;

  // School Age counts
  const schoolAgeAMSignedIn = schoolAgeSignIns.filter(s => s.amParentDropOffTime && !s.stayAllDay).length;
  const schoolAgePMDropOff = schoolAgeSignIns.filter(s => s.pmSchoolAgeDropOffTime).length;
  const schoolAgePMOut = schoolAgeSignIns.filter(s => s.pmParentPickupTime && !s.stayAllDay).length;

  // Create children lookup — must be declared BEFORE any code that uses it (line 500+)
  const childrenById = {};
  allChildren.forEach(c => { childrenById[c.id] = c; });

  const pastReminderNames = pastReminderChildIds.map(id => childrenById[id]?.name).filter(Boolean);

  // Stay All Day — School Age children counted in Full Time ratio
  const stayAllDaySignIns = schoolAgeSignIns.filter(s => s.stayAllDay === true);
  const stayAllDayCurrentlyIn = stayAllDaySignIns.filter(s => s.amParentDropOffTime && !s.pmParentPickupTime);
  const stayAllDayCurrentlyInNames = stayAllDayCurrentlyIn.map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');
  const stayAllDaySignedOut = stayAllDaySignIns.filter(s => s.pmParentPickupTime);
  const stayAllDaySignedOutNames = stayAllDaySignedOut.map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');

  // Mid-Day Departure — all categories
  const allCategorySignIns = [...fullTimeSignIns, ...partTimeSignIns, ...schoolAgeSignIns, ...afterSchoolSignIns];
  const midDayCurrentlyOut = allCategorySignIns.filter(s => s.midDayOut?.outTime && !s.midDayOut?.returnTime);
  const midDayReturnedToday = allCategorySignIns.filter(s => s.midDayOut?.outTime && s.midDayOut?.returnTime);

  // Part Time counts
  const partTimeSignedIn = partTimeSignIns.filter(s => s.dropOffTime && !s.pickUpTime).length;
  const partTimeSignedOut = partTimeSignIns.filter(s => s.dropOffTime && s.pickUpTime).length;

  // After School counts
  const afterSchoolSignedIn = afterSchoolSignIns.filter(s => s.dropOffTime && !s.pickUpTime).length;
  const afterSchoolSignedOut = afterSchoolSignIns.filter(s => s.dropOffTime && s.pickUpTime).length;

  // Subcategory counts - ALL Full Time children who came today (matches School Age behavior)
  // Handle both old format ('PreK') and new format ('prek')
  const signedInFullTimeChildren = fullTimeSignIns.filter(s => s.dropOffTime);
  const signedOutFullTimeChildren = fullTimeSignIns.filter(s => s.dropOffTime && s.pickUpTime);
  const currentlyInFullTimeChildren = fullTimeSignIns.filter(s => s.dropOffTime && !s.pickUpTime);

  const _matchSubcat = (s, names) => {
    const subcat = childrenById[s.childId]?.fullTimeSubcategory;
    return names.includes(subcat);
  };

  const preKCount = signedInFullTimeChildren.filter(s => _matchSubcat(s, ['prek', 'PreK'])).length;
  const youngToddlerCount = signedInFullTimeChildren.filter(s => _matchSubcat(s, ['young_toddler', 'Young Toddler'])).length;
  const olderToddlerCount = signedInFullTimeChildren.filter(s => _matchSubcat(s, ['older_toddler', 'Older Toddler'])).length;
  const infantCount = signedInFullTimeChildren.filter(s => _matchSubcat(s, ['infant', 'Infant'])).length;

  // Currently-in by Full Time subcategory (used by "Still in Center" panel — does NOT alter top cards)
  const preKInCount = currentlyInFullTimeChildren.filter(s => _matchSubcat(s, ['prek', 'PreK'])).length;
  const youngToddlerInCount = currentlyInFullTimeChildren.filter(s => _matchSubcat(s, ['young_toddler', 'Young Toddler'])).length;
  const olderToddlerInCount = currentlyInFullTimeChildren.filter(s => _matchSubcat(s, ['older_toddler', 'Older Toddler'])).length;
  const infantInCount = currentlyInFullTimeChildren.filter(s => _matchSubcat(s, ['infant', 'Infant'])).length;

  // ============ CHILDREN NAME LISTS FOR TOOLTIPS ============
  // Full Time - Signed In Today (all currently signed in)
  const fullTimeSignedInNames = signedInFullTimeChildren.map(s => 
    childrenById[s.childId]?.name || s.childName || 'Unknown'
  );
  // Full Time - Signed Out Today
  const fullTimeSignedOutNames = signedOutFullTimeChildren.map(s => 
    childrenById[s.childId]?.name || s.childName || 'Unknown'
  );
  // Full Time Subcategories
  const preKNames = signedInFullTimeChildren.filter(s => {
    const subcat = childrenById[s.childId]?.fullTimeSubcategory;
    return subcat === 'prek' || subcat === 'PreK';
  }).map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');
  
  const youngToddlerNames = signedInFullTimeChildren.filter(s => {
    const subcat = childrenById[s.childId]?.fullTimeSubcategory;
    return subcat === 'young_toddler' || subcat === 'Young Toddler';
  }).map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');
  
  const olderToddlerNames = signedInFullTimeChildren.filter(s => {
    const subcat = childrenById[s.childId]?.fullTimeSubcategory;
    return subcat === 'older_toddler' || subcat === 'Older Toddler';
  }).map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');
  
  const infantNames = signedInFullTimeChildren.filter(s => {
    const subcat = childrenById[s.childId]?.fullTimeSubcategory;
    return subcat === 'infant' || subcat === 'Infant';
  }).map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');

  // Signed Out Full Time — subcategory counts and names (for new stacked card)
  const signedOutPreKCount = signedOutFullTimeChildren.filter(s => {
    const subcat = childrenById[s.childId]?.fullTimeSubcategory;
    return subcat === 'prek' || subcat === 'PreK';
  }).length;
  const signedOutYoungToddlerCount = signedOutFullTimeChildren.filter(s => {
    const subcat = childrenById[s.childId]?.fullTimeSubcategory;
    return subcat === 'young_toddler' || subcat === 'Young Toddler';
  }).length;
  const signedOutOlderToddlerCount = signedOutFullTimeChildren.filter(s => {
    const subcat = childrenById[s.childId]?.fullTimeSubcategory;
    return subcat === 'older_toddler' || subcat === 'Older Toddler';
  }).length;
  const signedOutInfantCount = signedOutFullTimeChildren.filter(s => {
    const subcat = childrenById[s.childId]?.fullTimeSubcategory;
    return subcat === 'infant' || subcat === 'Infant';
  }).length;

  const signedOutPreKNames = signedOutFullTimeChildren.filter(s => {
    const subcat = childrenById[s.childId]?.fullTimeSubcategory;
    return subcat === 'prek' || subcat === 'PreK';
  }).map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');
  const signedOutYoungToddlerNames = signedOutFullTimeChildren.filter(s => {
    const subcat = childrenById[s.childId]?.fullTimeSubcategory;
    return subcat === 'young_toddler' || subcat === 'Young Toddler';
  }).map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');
  const signedOutOlderToddlerNames = signedOutFullTimeChildren.filter(s => {
    const subcat = childrenById[s.childId]?.fullTimeSubcategory;
    return subcat === 'older_toddler' || subcat === 'Older Toddler';
  }).map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');
  const signedOutInfantNames = signedOutFullTimeChildren.filter(s => {
    const subcat = childrenById[s.childId]?.fullTimeSubcategory;
    return subcat === 'infant' || subcat === 'Infant';
  }).map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');

  // School Age - children names
  const schoolAgeAMSignedInNames = schoolAgeSignIns.filter(s => s.amParentDropOffTime && !s.stayAllDay)
    .map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');
  const schoolAgePMDropOffNames = schoolAgeSignIns.filter(s => s.pmSchoolAgeDropOffTime)
    .map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');
  const schoolAgePMOutNames = schoolAgeSignIns.filter(s => s.pmParentPickupTime && !s.stayAllDay)
    .map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');

  // Part Time - children names
  const partTimeAllNames = partTimeSignIns.map(s => 
    childrenById[s.childId]?.name || s.childName || 'Unknown'
  );
  const partTimeSignedInNames = partTimeSignIns.filter(s => s.dropOffTime && !s.pickUpTime)
    .map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');
  const partTimeSignedOutNames = partTimeSignIns.filter(s => s.dropOffTime && s.pickUpTime)
    .map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');

  // After School - children names  
  const afterSchoolAllNames = afterSchoolSignIns.map(s => 
    childrenById[s.childId]?.name || s.childName || 'Unknown'
  );
  const afterSchoolSignedInNames = afterSchoolSignIns.filter(s => s.dropOffTime && !s.pickUpTime)
    .map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');
  const afterSchoolSignedOutNames = afterSchoolSignIns.filter(s => s.dropOffTime && s.pickUpTime)
    .map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');

  // Home dashboard summary bar totals — declared last so all dependencies exist
  const homeTotalSignedIn = fullTimeSignedIn + stayAllDayCurrentlyIn.length + partTimeSignedIn +
    schoolAgeSignIns.filter(s => ((s.amParentDropOffTime && !s.amBusDepartureTime) || s.pmSchoolAgeDropOffTime) && !s.pmParentPickupTime && !s.stayAllDay).length +
    afterSchoolSignedIn;
  const homeTotalSignedOut = fullTimeSignedOut + stayAllDaySignedOut.length + partTimeSignedOut + schoolAgePMOut + afterSchoolSignedOut;
  const homeTotalSignedInNames = [
    ...fullTimeSignedInNames,
    ...stayAllDayCurrentlyInNames,
    ...partTimeSignedInNames,
    ...schoolAgeSignIns.filter(s => ((s.amParentDropOffTime && !s.amBusDepartureTime) || s.pmSchoolAgeDropOffTime) && !s.pmParentPickupTime && !s.stayAllDay)
      .map(s => childrenById[s.childId]?.name || s.childName || 'Unknown'),
    ...afterSchoolSignedInNames,
  ];
  const homeTotalSignedOutNames = [
    ...fullTimeSignedOutNames,
    ...stayAllDaySignedOutNames,
    ...partTimeSignedOutNames,
    ...schoolAgePMOutNames,
    ...afterSchoolSignedOutNames,
  ];

  // ===== "Still In Center By Category" data (ratio compliance view) =====
  const schoolAgeAMOnlyIn = schoolAgeSignIns.filter(s =>
    s.amParentDropOffTime && !s.pmSchoolAgeDropOffTime && !s.pmParentPickupTime && !s.stayAllDay && !s.amBusDepartureTime
  ).length;
  const schoolAgePMOnlyIn = schoolAgeSignIns.filter(s =>
    s.pmSchoolAgeDropOffTime && !s.pmParentPickupTime
  ).length;
  const schoolAgeAMCame = schoolAgeSignIns.filter(s => s.amParentDropOffTime && !s.stayAllDay).length;
  const schoolAgePMCame = schoolAgeSignIns.filter(s => s.pmSchoolAgeDropOffTime).length;
  const partTimeStillIn = partTimeSignIns.filter(s => s.dropOffTime && !s.pickUpTime).length;
  const partTimeOut = partTimeSignIns.filter(s => s.dropOffTime && s.pickUpTime).length;
  const afterSchoolStillIn = afterSchoolSignIns.filter(s => s.dropOffTime && !s.pickUpTime).length;
  const afterSchoolOut = afterSchoolSignIns.filter(s => s.dropOffTime && s.pickUpTime).length;

  // Helper: current-in subcategory names (Full Time) — for the still-here popover
  const _ftSubcatNames = (predList) => predList.map(s => childrenById[s.childId]?.name || s.childName || 'Unknown');
  const preKInNames = _ftSubcatNames(currentlyInFullTimeChildren.filter(s => _matchSubcat(s, ['prek', 'PreK'])));
  const youngToddlerInNames = _ftSubcatNames(currentlyInFullTimeChildren.filter(s => _matchSubcat(s, ['young_toddler', 'Young Toddler'])));
  const olderToddlerInNames = _ftSubcatNames(currentlyInFullTimeChildren.filter(s => _matchSubcat(s, ['older_toddler', 'Older Toddler'])));
  const infantInNames = _ftSubcatNames(currentlyInFullTimeChildren.filter(s => _matchSubcat(s, ['infant', 'Infant'])));

  const stillInData = {
    fullTime: {
      stillIn: currentlyInFullTimeChildren.length + stayAllDayCurrentlyIn.length,
      total: signedInFullTimeChildren.length + stayAllDaySignIns.length,
      rows: [
        { label: 'PreK', value: preKInCount, names: preKInNames },
        { label: 'Young Toddler', value: youngToddlerInCount, names: youngToddlerInNames },
        { label: 'Older Toddler', value: olderToddlerInCount, names: olderToddlerInNames },
        { label: 'Infant', value: infantInCount, names: infantInNames },
        ...(stayAllDayCurrentlyIn.length > 0
          ? [{ label: 'School Age Staying All Day', value: stayAllDayCurrentlyIn.length, names: stayAllDayCurrentlyIn.map(s => childrenById[s.childId]?.name || s.childName || 'Unknown') }]
          : []),
      ],
    },
    schoolAge: amBusPickUpTime ? {
      // AM bus has picked up — donut resets and tracks PM drop-offs going forward
      stillIn: schoolAgePMOnlyIn,
      total: schoolAgePMCame,
      rows: [
        { label: 'Still Here', value: schoolAgePMOnlyIn, names: schoolAgeSignIns.filter(s => s.pmSchoolAgeDropOffTime && !s.pmParentPickupTime).map(s => childrenById[s.childId]?.name || s.childName || 'Unknown') },
      ],
    } : {
      // Before AM bus pickup — show AM kids physically at the center waiting for bus
      stillIn: schoolAgeAMOnlyIn,
      total: schoolAgeAMCame,
      rows: [
        { label: 'Still Here', value: schoolAgeAMOnlyIn, names: schoolAgeSignIns.filter(s => s.amParentDropOffTime && !s.pmSchoolAgeDropOffTime && !s.pmParentPickupTime && !s.stayAllDay && !s.amBusDepartureTime).map(s => childrenById[s.childId]?.name || s.childName || 'Unknown') },
      ],
    },
    partTime: {
      stillIn: partTimeStillIn,
      total: partTimeStillIn + partTimeOut,
      rows: [
        { label: 'Still Here', value: partTimeStillIn, names: partTimeSignIns.filter(s => s.dropOffTime && !s.pickUpTime).map(s => childrenById[s.childId]?.name || s.childName || 'Unknown') },
      ],
    },
    afterSchool: {
      stillIn: afterSchoolStillIn,
      total: afterSchoolStillIn + afterSchoolOut,
      rows: [
        { label: 'Still Here', value: afterSchoolStillIn, names: afterSchoolSignIns.filter(s => s.dropOffTime && !s.pickUpTime).map(s => childrenById[s.childId]?.name || s.childName || 'Unknown') },
      ],
    },
    pastReminderChildNames: pastReminderNames,
  };

  return (
    <AppShell pageTitle={getPageTitle()} breadcrumb>
      {/* Hero Carousel - Auto-fading images at top */}
      <div className="relative z-10 -m-4 lg:-m-6 mb-0">
        <HeroCarousel userName={user?.name?.split(' ')[0]} />
      </div>
      
      {/* Trial Banner if applicable - above gradient */}
      <div className="relative z-10">
        <TrialBanner />
      </div>
      
      {/* Blue gradient background - applied to content area */}
      <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6 bg-gradient-to-b from-sky-100 via-blue-50/60 to-white -mt-2">
        <div className="space-y-4 relative">
        {/* KPI Cards Row */}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard 
            title={language === 'es' ? 'Total de Niños' : 'Total Enrolled Children'}
            value={totalChildren} 
            tag={subscription?.status === 'expired' ? 'Trial Ended'
              : subscription?.status === 'trial' ? 'Free Trial'
              : subscription?.status === 'active' && subscription?.plan
                ? subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1) 
                : 'Free Trial'}
            tagStyle={subscription?.plan?.toLowerCase() === 'signature' 
              ? 'bg-purple-100 text-purple-800' 
              : 'bg-brand-highlight/20 text-ink-main'}
            icon={Users}
          />
          
          <div className="flex flex-col gap-4">
            <StatGroupCard
              title={language === 'es' ? 'Tiempo Completo' : 'Full Time'}
              color="text-brand-primary"
              icon={Users}
              lines={[
                { label: language === 'es' ? 'Registrados Hoy' : 'Signed In Today', value: fullTimeSignedIn, children: fullTimeSignedInNames },
                { label: 'PreK', value: preKCount, children: preKNames },
                { label: language === 'es' ? 'Niño Pequeño' : 'Young Toddler', value: youngToddlerCount, children: youngToddlerNames },
                { label: language === 'es' ? 'Niño Mayor' : 'Older Toddler', value: olderToddlerCount, children: olderToddlerNames },
                { label: language === 'es' ? 'Bebé' : 'Infant', value: infantCount, children: infantNames },
              ]}
            />
            <StatGroupCard
              title={language === 'es' ? 'Tiempo Completo — Salida por Categoría' : 'Full Time — Signed Out by Category'}
              color="text-emerald-700"
              icon={Users}
              lines={[
                { label: language === 'es' ? 'Total Salidos Hoy' : 'Signed Out Today', value: fullTimeSignedOut, children: fullTimeSignedOutNames },
                { label: 'PreK', value: signedOutPreKCount, children: signedOutPreKNames },
                { label: language === 'es' ? 'Niño Pequeño' : 'Young Toddler', value: signedOutYoungToddlerCount, children: signedOutYoungToddlerNames },
                { label: language === 'es' ? 'Niño Mayor' : 'Older Toddler', value: signedOutOlderToddlerCount, children: signedOutOlderToddlerNames },
                { label: language === 'es' ? 'Bebé' : 'Infant', value: signedOutInfantCount, children: signedOutInfantNames },
              ]}
            />
          </div>
          
          <StatGroupCard
            title={language === 'es' ? 'Edad Escolar' : 'School Age'}
            color="text-brand-secondary"
            lines={[
              { label: language === 'es' ? 'AM Registrados' : 'AM Signed In Today', value: schoolAgeAMSignedIn, children: schoolAgeAMSignedInNames },
              { label: language === 'es' ? 'Autobús A.M.' : 'AM Bus Pick Up', value: amBusPickUpTime || '—', children: [] },
              { label: language === 'es' ? 'PM Llegada' : 'PM Drop Off Today', value: schoolAgePMDropOff, children: schoolAgePMDropOffNames },
              { label: language === 'es' ? 'PM Salida' : 'PM Out', value: schoolAgePMOut, children: schoolAgePMOutNames },
            ]}
          />
          
          <StatGroupCard
            title={language === 'es' ? 'Part Time & After School' : 'Part Time & After School'}
            color="text-brand-accent"
            lines={[
              { label: language === 'es' ? 'Medio Tiempo — Entrada' : 'Part Time — In', value: partTimeSignedIn, children: partTimeSignedInNames },
              { label: language === 'es' ? 'Medio Tiempo — Salida' : 'Part Time — Out', value: partTimeSignedOut, children: partTimeSignedOutNames },
              { label: language === 'es' ? 'Después Escuela — Entrada' : 'After School — In', value: afterSchoolSignedIn, children: afterSchoolSignedInNames },
              { label: language === 'es' ? 'Después Escuela — Salida' : 'After School — Out', value: afterSchoolSignedOut, children: afterSchoolSignedOutNames },
            ]}
          />
        </div>

        {/* Who Is Still In The Center By Category — ratio compliance view */}
        <StillInCenterByCategory {...stillInData} />

        {/* Today's Movement Summary Bar — mirrors Daily Sign-In total bar */}
        <div
          className="rounded-2xl p-4 flex flex-wrap justify-between items-center gap-3"
          style={{
            background: 'linear-gradient(135deg, rgba(219,234,254,0.95) 0%, rgba(191,219,254,0.9) 50%, rgba(224,231,255,0.95) 100%)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: '0 8px 32px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.6)',
            border: '1px solid rgba(59,130,246,0.3)',
            position: 'relative',
            zIndex: 20,
          }}
        >
          <span className="text-blue-900 font-bold text-lg">
            TOTAL CHILDREN: {totalChildren}
          </span>
          <div className="flex gap-4 flex-wrap items-baseline">
            <HoverCount
              label={language === 'es' ? 'Registrados' : 'Signed In'}
              count={homeTotalSignedIn}
              children={homeTotalSignedInNames}
              colorClass="text-green-700 font-semibold"
            />
            <HoverCount
              label={language === 'es' ? 'Salieron' : 'Signed Out'}
              count={homeTotalSignedOut}
              children={homeTotalSignedOutNames}
              colorClass="text-blue-700 font-semibold"
            />
            {stayAllDayCurrentlyIn.length > 0 && (
              <HoverCount
                label="Staying All Day"
                count={stayAllDayCurrentlyIn.length}
                children={stayAllDayCurrentlyInNames}
                colorClass="text-emerald-700 font-semibold"
              />
            )}
            {midDayCurrentlyOut.length > 0 && (
              <HoverCount
                label="Mid-Day Out"
                count={midDayCurrentlyOut.length}
                children={midDayCurrentlyOut.map(s => s.childName || 'Unknown')}
                colorClass="text-amber-700 font-semibold"
              />
            )}
            {midDayReturnedToday.length > 0 && (
              <HoverCount
                label="Mid-Day Returned"
                count={midDayReturnedToday.length}
                children={midDayReturnedToday.map(s => s.childName || 'Unknown')}
                colorClass="text-emerald-700 font-semibold"
              />
            )}
          </div>
        </div>

        {/* Getting Started Card - Collapsible with rainbow gradient */}
        <section className="bg-gradient-to-r from-red-100 via-yellow-100 via-green-100 via-blue-100 to-purple-100 rounded-xl border-2 border-transparent overflow-hidden shadow-lg" style={{background: 'linear-gradient(90deg, rgba(254,202,202,0.7) 0%, rgba(254,240,138,0.7) 20%, rgba(187,247,208,0.7) 40%, rgba(191,219,254,0.7) 60%, rgba(233,213,255,0.7) 80%, rgba(252,205,211,0.7) 100%)'}}>
          <button
            onClick={() => setGettingStartedOpen(!gettingStartedOpen)}
            className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/60 rounded-lg border border-white/80 shadow-sm">
                <Lightbulb className="w-5 h-5 text-amber-500" />
              </div>
              <h2 className="text-lg font-semibold text-gray-800">
                {language === 'es' ? 'Cómo Empezar' : 'How to Get Started'}
              </h2>
            </div>
            <ChevronDown className={`w-5 h-5 text-gray-600 transition-transform duration-300 ${gettingStartedOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {gettingStartedOpen && (
            <div className="px-5 pb-5 border-t border-white/50 bg-white/50">
              <ol className="mt-4 space-y-3">

                {/* Step 1 — Add Staff Members */}
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-gradient-to-r from-red-400 to-orange-400 text-white rounded-full flex items-center justify-center text-sm font-bold shadow">1</span>
                  <div className="text-sm text-gray-700 pt-0.5">
                    <span>
                      {language === 'es'
                        ? 'Vaya a "Administrar Personal" para agregar a su equipo usando su correo electrónico y una contraseña temporal que pueden cambiar al iniciar sesión. Puede agregar personal manualmente o usar la Carga Masiva. En el área de Cumplimiento y Capacitación, puede cargar o solicitar cargas de fechas de certificación y archivos de autorización del personal.'
                        : 'Go to "Manage Staff" to add your team using their email address and a temporary password they can change after their first login. You can add staff manually one at a time, or use Bulk Upload to add multiple staff at once. In the Compliance & Training area, you can upload or request uploads of staff certification dates and clearance files.'}
                    </span>
                    <div className="mt-2 p-2 bg-white/70 rounded-lg border border-amber-200">
                      <span className="text-amber-600 font-medium">💡 {language === 'es' ? 'Consejo sobre Personal Compartido:' : 'Shared Staff Tip:'}</span>
                      <span className="block mt-1 text-gray-600">
                        {language === 'es'
                          ? 'Considere crear un inicio de sesión genérico con rol "Personal" (p. ej., personal@suguardería.com) que su equipo de recepción comparta en una tableta — para evitar que el personal vea los registros de cumplimiento, archivos o facturación de otros.'
                          : 'Consider creating one generic "Staff" role login (e.g., staff@yourdaycare.com) that your front-desk team shares on a tablet — to prevent staff from viewing other staff\'s compliance records, files, or billing.'}
                      </span>
                    </div>
                  </div>
                </li>

                {/* Step 2 — Add Children */}
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-gradient-to-r from-yellow-400 to-green-400 text-white rounded-full flex items-center justify-center text-sm font-bold shadow">2</span>
                  <div className="text-sm text-gray-700 pt-0.5">
                    <span>
                      {language === 'es'
                        ? 'Vaya a "Niños Matriculados" para agregar a sus niños.'
                        : 'Go to "Enrolled Children" to add your enrolled children.'}
                    </span>
                    <div className="mt-2 p-2 bg-white/70 rounded-lg border border-amber-200">
                      <span className="text-amber-600 font-medium">💡 {language === 'es' ? 'Consejo para ahorrar tiempo:' : 'Time-Saving Tip:'}</span>
                      <span className="block mt-1 text-gray-600">
                        {language === 'es'
                          ? 'Si tiene varios niños, use la función de Carga Masiva — descargue la plantilla Excel, complete la información de cada niño y cárguela de una vez. En el área de Registros de Cumplimiento también puede cargar archivos de los niños y fechas clave (registros de vacunación, fechas de inscripción).'
                          : 'If you have multiple children, use the Bulk Upload feature — download the Excel template, fill in each child\'s information, and upload all at once. In the Compliance Records area, you can also upload children\'s files and key dates (immunization records, enrollment dates).'}
                      </span>
                    </div>
                  </div>
                </li>

                {/* Step 3 — Select a date */}
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-gradient-to-r from-green-400 to-blue-400 text-white rounded-full flex items-center justify-center text-sm font-bold shadow">3</span>
                  <span className="text-sm text-gray-700 pt-0.5">{language === 'es' ? 'Seleccione una fecha del calendario para registrar entradas/salidas' : 'Select a date from the calendar to begin logging sign-ins/outs'}</span>
                </li>

                {/* Step 4 — Choose drop-off/pick-up */}
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-gradient-to-r from-blue-400 to-purple-400 text-white rounded-full flex items-center justify-center text-sm font-bold shadow">4</span>
                  <span className="text-sm text-gray-700 pt-0.5">{language === 'es' ? 'Elija la persona de entrega/recogida del menú' : 'Choose the drop-off/pick-up person from the dropdown'}</span>
                </li>

                {/* Step 5 — Time captured automatically */}
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-gradient-to-r from-purple-400 to-pink-400 text-white rounded-full flex items-center justify-center text-sm font-bold shadow">5</span>
                  <span className="text-sm text-gray-700 pt-0.5">{language === 'es' ? 'El tiempo se captura automáticamente' : 'Time is captured automatically'}</span>
                </li>

              </ol>

              {/* PDF Field Name Guide callout — Stage 1 */}
              <div className="mt-4 rounded-lg border-2 border-teal-300 bg-white/80 p-3 flex items-start gap-3">
                <div className="p-1.5 bg-teal-50 rounded-md border border-teal-200 flex-shrink-0">
                  <FileText className="w-4 h-4 text-teal-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-teal-900 flex items-center gap-2">
                    {language === 'es' ? 'Guía de Nombres de Campos PDF' : 'PDF Field Name Guide'}
                    <span className="relative inline-flex">
                      <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-pink-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-pink-500"></span>
                    </span>
                    <span className="text-[10px] font-bold text-pink-600 bg-pink-100 rounded px-1.5 py-0.5">NEW</span>
                  </div>
                  <p className="text-xs text-gray-700 mt-0.5">
                    {language === 'es'
                      ? 'Cree PDFs de inscripción en Adobe Acrobat Pro que se llenen automáticamente. Copie los nombres exactos de campos para su guía.'
                      : 'Building enrollment PDFs in Adobe Acrobat Pro so they auto-fill? Copy the exact field names from this guide.'}
                  </p>
                  <a
                    href="/child-records?tab=enrollment"
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-teal-700 hover:text-teal-900 bg-white border border-teal-300 rounded-md px-3 py-1.5 hover:bg-teal-50"
                    data-testid="homepage-pdf-guide-link"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    {language === 'es' ? 'Abrir Guía' : 'Open Guide in Records Management'}
                  </a>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Tabs + Content Section with blue theme */}
        <section className="bg-white rounded-xl shadow-card border-2 border-blue-300 overflow-hidden hover:border-blue-400 transition-all">
          {/* Tab Navigation - Enhanced with blue gradient background */}
          <div className="p-4 border-b border-blue-200 bg-gradient-to-r from-blue-50 via-white to-blue-50 overflow-x-auto">
            <div className="flex flex-nowrap gap-3 min-w-max justify-center">
              <TabButton 
                label={language === 'es' ? 'Calendario' : 'Calendar'}
                icon={CalendarIcon}
                active={activeTab === "calendar"} 
                onClick={() => setActiveTab("calendar")}
                isCalendar={true}
              />
              <TabButton 
                label={language === 'es' ? 'Lista Diaria' : 'Daily List'}
                icon={ListChecks}
                active={activeTab === "daily"} 
                onClick={() => setActiveTab("daily")} 
              />
              {/* Feedback - Owner/Co-owner only */}
              {isOwner && (
                <TabButton 
                  label={language === 'es' ? 'Comentarios' : 'Feedback'}
                  icon={Lightbulb}
                  active={activeTab === "feedback"} 
                  onClick={() => setActiveTab("feedback")} 
                />
              )}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === "calendar" && (
              <div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-ink-main">
                      {language === 'es' ? 'Seleccione una Fecha para Comenzar' : 'Select a Date to Begin'}
                    </h3>
                    <p className="text-sm text-gray-700 font-medium mt-1">
                      {language === 'es' 
                        ? 'Haga clic en cualquier fecha para abrir la hoja de registro de ese día.' 
                        : 'Click on any date to open the sign-in sheet for that day.'}
                    </p>
                  </div>
                  <button
                    onClick={handleTodaySignIn}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-primary text-white font-medium hover:opacity-95 transition-opacity shadow-sm"
                    data-testid="quick-today-btn"
                  >
                    <CalendarIcon className="w-4 h-4" />
                    {language === 'es' ? 'Ir a Hoy' : 'Go to Today'} ({format(new Date(), 'MMM d')})
                  </button>
                </div>

                <div className="flex justify-center">
                  <div 
                    className="rounded-xl p-5 border-2 border-white/50 shadow-lg"
                    style={{background: 'linear-gradient(135deg, rgba(254,202,202,0.6) 0%, rgba(254,240,138,0.6) 20%, rgba(187,247,208,0.6) 40%, rgba(191,219,254,0.6) 60%, rgba(233,213,255,0.6) 80%, rgba(252,205,211,0.6) 100%)'}}
                  >
                    <div className="bg-white/90 rounded-lg p-3 border border-gray-200">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={handleDateSelect}
                        className="rounded-lg"
                        disabled={isViewOnly ? () => true : undefined}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "daily" && (
              <DailyListTab />
            )}

            {activeTab === "feedback" && isOwner && (
              <FeedbackSuggestionsTab />
            )}
          </div>
        </section>
      </div>
      </div>
      
      {/* Version Indicator */}
      <div className="fixed bottom-2 left-2 text-xs text-gray-500 bg-white/90 px-2 py-1 rounded shadow-sm border border-gray-200 z-50">
        v3.2 | Mar 2, 2025 - 2:15PM
      </div>
    </AppShell>
  );
};

export default HomePageNew;
